/**
 * Countdown and kickoff formatting for Home (specs/home-page AC-9, AC-13, AC-26).
 * Pure: callers pass `nowMs` so tests and Storybook can freeze the clock.
 */

import { HOME_COPY } from '../constants/home.constants';

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** "Sun 16:30" in the device's time zone (en-GB keeps the 24h clock and the bare weekday). */
const KICKOFF_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** Weekday + local time, e.g. "Sun 16:30". Empty string for an invalid date. */
export function formatMatchKickoff(kickoffIso: string): string {
  const date = new Date(kickoffIso);
  if (Number.isNaN(date.getTime())) return '';
  // en-GB renders "Sun 16:30" on most engines and "Sun, 16:30" on some; normalise the comma away.
  return KICKOFF_FORMATTER.format(date).replace(',', '');
}

/**
 * Slate countdown to the next UTC midnight (AC-9): "{H}h", or "{M}m" under an hour; "now" once due.
 */
export function formatNewIn(nextIso: string, nowMs: number): string {
  const nextMs = new Date(nextIso).getTime();
  if (Number.isNaN(nextMs)) return '';
  const diff = nextMs - nowMs;
  if (diff <= 0) return HOME_COPY.SLATE_NEW_NOW;
  if (diff < HOUR_MS) return `${Math.max(1, Math.floor(diff / MINUTE_MS))}m`;
  return `${Math.floor(diff / HOUR_MS)}h`;
}

/**
 * Weekly card countdown to the week's end (AC-22): "{D}d {H}h", or "{H}h {M}m" under a day; "now" once due.
 */
export function formatWeekCountdown(endIso: string, nowMs: number): string {
  const endMs = new Date(endIso).getTime();
  if (Number.isNaN(endMs)) return '';
  const diff = endMs - nowMs;
  if (diff <= 0) return HOME_COPY.SLATE_NEW_NOW;
  if (diff >= DAY_MS) {
    const days = Math.floor(diff / DAY_MS);
    const hours = Math.floor((diff % DAY_MS) / HOUR_MS);
    return `${days}d ${hours}h`;
  }
  const hours = Math.floor(diff / HOUR_MS);
  const minutes = Math.floor((diff % HOUR_MS) / MINUTE_MS);
  return `${hours}h ${minutes}m`;
}

/**
 * Hot-row countdown: "{H}h {M}m" under 24h ("{M}m" under an hour), otherwise the weekday + time.
 * A kickoff already in the past reads as live.
 */
export function formatKickoffCountdown(kickoffIso: string, nowMs: number): string {
  const kickoffMs = new Date(kickoffIso).getTime();
  if (Number.isNaN(kickoffMs)) return '';
  const diff = kickoffMs - nowMs;
  if (diff <= 0) return HOME_COPY.KICKOFF_STARTED;
  if (diff >= DAY_MS) return formatMatchKickoff(kickoffIso);
  const hours = Math.floor(diff / HOUR_MS);
  const minutes = Math.floor((diff % HOUR_MS) / MINUTE_MS);
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}
